import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { paymentService, authService } from '../services/api';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [paymentStatus, setPaymentStatus] = useState(null); // 'pending', 'completed', 'failed'
  const [currentReference, setCurrentReference] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    let interval;
    if (paymentStatus === 'pending' && currentReference) {
      interval = setInterval(checkStatus, 5000);
    }
    return () => clearInterval(interval);
  }, [paymentStatus, currentReference]);

  const fetchHistory = async () => {
    try {
      const response = await paymentService.getHistory();
      setTransactions(response.data.transactions);
    } catch (err) {
      console.error('Failed to fetch history', err);
    }
  };

  const checkStatus = async () => {
    if (!currentReference) return;
    try {
      const response = await paymentService.checkStatus(currentReference);
      if (response.data.status === 'completed') {
        setPaymentStatus('completed');
        setSuccess('Paiement réussi ! Votre abonnement est actif.');
        setPhoneNumber('');
        fetchHistory();
      } else if (response.data.status === 'failed') {
        setPaymentStatus('failed');
        setError('Le paiement a échoué.');
      }
    } catch (err) {
      console.error('Status check failed', err);
    }
  };

  const handlePayment = async () => {
    if (!phoneNumber) {
      setError('Veuillez entrer un numéro de téléphone');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setPaymentStatus('pending');

    try {
      const amount = selectedPlan === 'monthly' ? 5950 : 59500;
      const description = selectedPlan === 'monthly' ? 'Abonnement Mensuel ERA' : 'Abonnement Annuel ERA';

      const response = await paymentService.initiate({
        amount,
        phoneNumber,
        description
      });

      setCurrentReference(response.data.campayReference);
      setSuccess(`Paiement initié ! Veuillez valider sur votre téléphone (${response.data.ussdCode || 'USSD'}).`);
      setOpenConfirm(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'initiation du paiement');
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      id: 'monthly',
      title: 'Mensuel',
      price: '5,950 FCFA',
      period: '/ mois',
      features: ['Projets illimités', 'Tâches illimitées', 'Support prioritaire']
    },
    {
      id: 'yearly',
      title: 'Annuel',
      price: '59,500 FCFA',
      period: '/ an',
      features: ['2 mois offerts', 'Tout illimité', 'Badge Pro']
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Abonnement Premium
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Passez à la vitesse supérieure avec ERA Premium.
        </Typography>

        <Grid container spacing={4}>
          {/* Plans Section */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} key={plan.id}>
                  <Card 
                    sx={{ 
                      border: selectedPlan === plan.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.3s'
                    }}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {selectedPlan === plan.id && (
                      <Chip 
                        label="Sélectionné" 
                        color="primary" 
                        size="small" 
                        sx={{ position: 'absolute', top: 10, right: 10 }} 
                      />
                    )}
                    <CardHeader
                      title={plan.title}
                      titleTypographyProps={{ align: 'center' }}
                      subheaderTypographyProps={{ align: 'center' }}
                      sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}
                    />
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 2 }}>
                        <Typography component="h2" variant="h4" color="text.primary">
                          {plan.price}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                          {plan.period}
                        </Typography>
                      </Box>
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                        {plan.features.map((line) => (
                          <Typography component="li" variant="subtitle1" align="center" key={line}>
                            {line}
                          </Typography>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Paper sx={{ p: 3, mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Paiement via CamPay (Mobile Money)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                <TextField
                  label="Numéro de téléphone (ex: 2376xxxxxxxx)"
                  variant="outlined"
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={loading || paymentStatus === 'pending'}
                />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handlePayment}
                  disabled={loading || !phoneNumber || paymentStatus === 'pending'}
                  sx={{ minWidth: 200, height: 56 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Payer maintenant'}
                </Button>
              </Box>
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
              
              {paymentStatus === 'pending' && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="info.main">
                    En attente de validation sur votre téléphone...
                  </Typography>
                  <CircularProgress size={20} sx={{ mt: 1 }} />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* History Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Historique des transactions
              </Typography>
              <List>
                {transactions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Aucune transaction.
                  </Typography>
                ) : (
                  transactions.map((tx) => (
                    <React.Fragment key={tx.id}>
                      <ListItem>
                        <ListItemText
                          primary={`${tx.amount} ${tx.currency}`}
                          secondary={new Date(tx.created_at).toLocaleDateString()}
                        />
                        <Chip 
                          label={tx.status} 
                          color={tx.status === 'completed' ? 'success' : tx.status === 'pending' ? 'warning' : 'error'} 
                          size="small" 
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
          <DialogTitle>Validation du paiement</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Veuillez vérifier votre téléphone et valider la transaction Mobile Money.
              Une fois validé, votre abonnement sera activé automatiquement.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirm(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SubscriptionPage;
